import { useState, useRef, useEffect } from 'react';
import { Upload, Link as LinkIcon, X, Video } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { getFileUrl, useFileUpload } from '@/shared/hooks/useFileUpload';

type VideoInputMode = 'upload' | 'url';

type VideoInputToggleProps = {
  label: string;
  value?: string;
  onChange: (url: string) => void;
  error?: string;
  hint?: string;
  maxSizeMB?: number;
};

/**
 * VideoInputToggle - Dual-mode video input component
 * 
 * Supports two input modes:
 * 1. Upload: Direct file upload to S3 (up to 100MB)
 * 2. URL: External video URL (YouTube, Vimeo, etc.)
 * 
 * Features:
 * - Mode toggle (Upload / URL)
 * - Video format validation (mp4, webm, mov)
 * - Size limit validation (100MB default)
 * - URL format validation
 * - Preview indication
 * - Clear functionality
 * 
 * Usage:
 * - Introduction video URL field in ProfileMediaSection
 * 
 * Note: Actual S3 upload will be integrated with FileUploadService in Phase 4
 */
export function VideoInputToggle({
  label,
  value,
  onChange,
  error,
  hint,
  maxSizeMB = 100,
}: VideoInputToggleProps) {
  // Helper: Hydrate S3 file key to full URL
  const hydrateVideoUrl = (fileKey?: string): string | undefined => {
    if (!fileKey || fileKey.trim() === '') {
      return undefined;
    }
    
    // If already a full URL (starts with http) or blob URL, return as is
    if (fileKey.startsWith('http://') || fileKey.startsWith('https://') || fileKey.startsWith('blob:')) {
      return fileKey;
    }
    
    // Convert S3 file key to full URL
    return getFileUrl(fileKey);
  };

  // Store hydrated URL for display
  const [displayUrl, setDisplayUrl] = useState<string | undefined>(() => hydrateVideoUrl(value));

  // Update display URL when value changes
  useEffect(() => {
    setDisplayUrl(hydrateVideoUrl(value));
  }, [value]);

  const [mode, setMode] = useState<VideoInputMode>(() => {
    // Determine initial mode based on existing value
    if (value) {
      // If it starts with http/https, it's a URL (external or full S3 URL)
      // If not, it's an S3 file key from upload
      return value.startsWith('http') ? 'url' : 'upload';
    }
    return 'url'; // Default to URL mode
  });

  const [uploadError, setUploadError] = useState<string | undefined>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoFile, setVideoFile] = useState<File | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize file upload hook with progress tracking
  const { uploadAsync, isUploading } = useFileUpload({
    maxSizeMB,
    onProgress: (progress) => setUploadProgress(progress),
  });

  const handleModeChange = (newMode: VideoInputMode) => {
    setMode(newMode);
    setUploadError(undefined);
    onChange(''); // Clear value when switching modes
    setVideoFile(undefined);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(undefined);
    setUploadProgress(0);

    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setUploadError(`Video size must be less than ${maxSizeMB}MB`);
      return;
    }

    // Validate file type
    const validFormats = ['video/mp4', 'video/webm', 'video/quicktime']; // mp4, webm, mov
    if (!validFormats.includes(file.type)) {
      setUploadError('Invalid video format. Supported: MP4, WebM, MOV');
      return;
    }

    setVideoFile(file);

    // Upload file to S3
    try {
      // Upload to S3 and get back the file key
      const fileKey = await uploadAsync(file);
      
      // Store the file key (not the full URL) in the form
      // The backend will hydrate it to a full URL when needed
      onChange(fileKey);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      console.error('Upload error:', err);
      setVideoFile(undefined);
    }
  };

  const handleUrlChange = (url: string) => {
    onChange(url);
  };

  const handleClear = () => {
    onChange('');
    setVideoFile(undefined);
    setUploadError(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'upload' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleModeChange('upload')}
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload Video
        </Button>
        <Button
          type="button"
          variant={mode === 'url' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleModeChange('url')}
        >
          <LinkIcon className="mr-2 h-4 w-4" />
          Provide URL
        </Button>
      </div>

      {/* Upload Mode */}
      {mode === 'upload' && (
        <div className="space-y-3">
          <div className="flex items-start gap-4">
            {/* Preview Indicator */}
            <div className="flex h-24 w-32 flex-shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
              {videoFile || value ? (
                <Video className="h-8 w-8 text-blue-500" />
              ) : (
                <Video className="h-8 w-8 text-gray-400" />
              )}
            </div>

            {/* Controls */}
            <div className="flex-1 space-y-2">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFileClick}
                  disabled={isUploading}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploading ? 'Uploading...' : videoFile ? 'Change Video' : 'Select Video'}
                </Button>

                {(videoFile || value) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    disabled={isUploading}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>

              {videoFile && (
                <p className="text-sm text-gray-600">
                  {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(1)} MB)
                </p>
              )}

              {isUploading && (
                <div className="space-y-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600">Uploading... {uploadProgress}%</p>
                </div>
              )}

              {!isUploading && !uploadError && !error && (
                <p className="text-xs text-gray-500">
                  Supported formats: MP4, WebM, MOV (max {maxSizeMB}MB)
                </p>
              )}
              {(uploadError || error) && (
                <p className="text-sm text-red-600">{uploadError || error}</p>
              )}
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* URL Mode */}
      {mode === 'url' && (
        <div className="space-y-2">
          <Input
            type="url"
            value={displayUrl || ''}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className={error ? 'border-red-500' : ''}
          />
          {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
          {!error && !hint && (
            <p className="text-xs text-gray-500">
              External video URL (YouTube, Vimeo, direct link, etc.)
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}

